import React, { useState, useEffect } from 'react';
import StorageManager from '../services/StorageManager';
import { AnimatedFlameIcon } from './Icons';

function TopPicksSelector({ currentUser, allPilots, onComplete, onGoHome, onBackToPilots }) {
  const [selectedPicks, setSelectedPicks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [hoveredPilot, setHoveredPilot] = useState(null);
  const [expandedLogline, setExpandedLogline] = useState(null);

  // Pre-load existing picks so user can see/update their selections
  useEffect(() => {
    const loadExisting = async () => {
      if (!currentUser) return;
      const existing = await StorageManager.getTopPicks(currentUser.id);
      if (existing) {
        const picks = [existing.pick1PilotId, existing.pick2PilotId, existing.pick3PilotId].filter(Boolean);
        if (picks.length > 0) setSelectedPicks(picks);
      }
    };
    loadExisting();
  }, [currentUser]);

  const maxPicks = Math.min(3, allPilots.length);

  const togglePick = (pilotId) => {
    if (submitted) return;
    setSelectedPicks(prev => {
      if (prev.includes(pilotId)) return prev.filter(id => id !== pilotId);
      if (prev.length >= maxPicks) return prev;
      return [...prev, pilotId];
    });
  };

  const handleSubmit = async () => {
    if (selectedPicks.length !== maxPicks || !currentUser) return;
    setSubmitting(true);
    // Pad to 3 slots for the API (empty string if fewer than 3 pilots)
    const picks = [...selectedPicks];
    while (picks.length < 3) picks.push('');
    const result = await StorageManager.submitTopPicks(currentUser.id, picks);
    if (result.success) {
      setSubmitted(true);
    } else {
      // Still show completion even if backend fails (picks recorded locally)
      console.warn('Top picks API failed, showing completion anyway:', result.message);
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  const ghostBtn = (id, disabled) => ({
    background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px',
    padding: '0.8rem 2.5rem', color: hoveredBtn === id ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '0.9rem', fontWeight: 500, fontFamily: "'Cormorant Garamond', Georgia, serif",
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.25s ease', letterSpacing: '0.05em',
    borderColor: hoveredBtn === id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
    opacity: disabled ? 0.5 : 1
  });

  // Season Complete screen
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: '#000000', fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#fff', padding: '2rem', textAlign: 'center' }}>
        <style dangerouslySetInnerHTML={{ __html: `
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        ` }} />
        <div style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <AnimatedFlameIcon size={64} style={{ margin: '0 auto 2rem', display: 'block', opacity: 0.8 }} />
          <h2 style={{ fontSize: '1.4rem', fontWeight: 400, letterSpacing: '0.1em', marginBottom: '0.8rem',
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SEASON COMPLETE</h2>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', maxWidth: '400px', lineHeight: 1.6, marginBottom: '2rem',
            fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Thank you for reviewing this season's pilots.
          </p>
          <button onClick={onGoHome} style={{ ...ghostBtn('home', false), fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            onMouseEnter={() => setHoveredBtn('home')} onMouseLeave={() => setHoveredBtn(null)}>
            Back to Screening Room
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#000000',
      fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#fff' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      ` }} />

      <div style={{ flex: 1, padding: '3rem 1.5rem', maxWidth: '700px', margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem', animation: 'fadeInUp 0.5s ease-out' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 300, letterSpacing: '0.12em', marginBottom: '0.5rem',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{maxPicks === 1 ? 'SELECT YOUR FAVORITE' : `SELECT YOUR TOP ${maxPicks}`}</h2>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem', lineHeight: 1.6 }}>
            {maxPicks === 1
              ? 'Choose the pilot you\'d most like to see made into a series.'
              : `Choose the ${maxPicks === 2 ? 'two' : 'three'} pilots you'd most like to see made into a series. Selection order matters — pick your favorite first.`}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', marginTop: '0.3rem' }}>
            {selectedPicks.length} / {maxPicks} selected
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', animation: 'fadeInUp 0.5s ease-out 0.1s both' }}>
          {allPilots.map((pilot, i) => {
            const isSelected = selectedPicks.includes(pilot.id);
            const pickIndex = selectedPicks.indexOf(pilot.id);
            const isHovered = hoveredPilot === pilot.id;
            return (
              <React.Fragment key={pilot.id}>
                <div
                  onClick={() => togglePick(pilot.id)}
                  onMouseEnter={() => setHoveredPilot(pilot.id)}
                  onMouseLeave={() => setHoveredPilot(null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.9rem 1.2rem',
                    background: isSelected ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                    border: isSelected
                      ? '1px solid rgba(254,202,87,0.4)'
                      : isHovered ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.06)',
                    borderRadius: expandedLogline === pilot.id ? '10px 10px 0 0' : '10px',
                    cursor: selectedPicks.length >= maxPicks && !isSelected ? 'not-allowed' : 'pointer',
                    transition: 'all 0.25s ease', opacity: selectedPicks.length >= maxPicks && !isSelected ? 0.4 : 1
                  }}>
                  {/* Thumbnail */}
                  <div style={{ width: '60px', height: '34px', borderRadius: '6px', flexShrink: 0, overflow: 'hidden',
                    background: pilot.playbackId
                      ? `url(https://image.mux.com/${pilot.playbackId}/thumbnail.png?width=120&height=68&fit_mode=smartcrop) center/cover`
                      : 'linear-gradient(135deg, rgba(255,107,107,0.2), rgba(78,205,196,0.2))' }} />

                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.88rem', color: isSelected ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.6)',
                      fontWeight: isSelected ? 500 : 400 }}>
                      {pilot.pilotTitle}
                    </span>
                    {pilot.genre && (
                      <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem' }}>{pilot.genre}</span>
                    )}
                    {pilot.logline && (
                      <button onClick={(e) => { e.stopPropagation(); setExpandedLogline(expandedLogline === pilot.id ? null : pilot.id); }}
                        title="Show logline"
                        style={{ background: 'none', border: 'none', color: expandedLogline === pilot.id ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)', cursor: 'pointer',
                          padding: '2px', marginLeft: 'auto', flexShrink: 0, transition: 'color 0.2s', fontSize: '0.7rem',
                          fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                        onMouseOut={e => e.currentTarget.style.color = expandedLogline === pilot.id ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)'}>
                        logline
                      </button>
                    )}
                  </div>

                  {/* Selection indicator */}
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isSelected ? 'linear-gradient(135deg, #ff6b6b, #feca57, #4ecdc4)' : 'rgba(255,255,255,0.05)',
                    border: isSelected ? 'none' : '1px solid rgba(255,255,255,0.1)',
                    fontSize: '0.75rem', fontWeight: 600, color: isSelected ? '#000' : 'rgba(255,255,255,0.2)',
                    transition: 'all 0.25s ease' }}>
                    {isSelected ? pickIndex + 1 : ''}
                  </div>
                </div>
                {/* Expanded logline */}
                {expandedLogline === pilot.id && pilot.logline && (
                  <div style={{ padding: '0.6rem 1.2rem', background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)', borderTop: 'none',
                    borderRadius: '0 0 10px 10px', marginTop: '-1px' }}>
                    <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', lineHeight: 1.5,
                      fontFamily: "'Cormorant Garamond', Georgia, serif", margin: 0 }}>{pilot.logline}</p>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Submit */}
        <div style={{ textAlign: 'center', marginTop: '2rem', animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>
          <button onClick={handleSubmit}
            disabled={selectedPicks.length !== maxPicks || submitting}
            style={ghostBtn('submit', selectedPicks.length !== maxPicks || submitting)}
            onMouseEnter={() => setHoveredBtn('submit')} onMouseLeave={() => setHoveredBtn(null)}>
            {submitting ? 'Submitting...' : `Submit Top ${maxPicks}`}
          </button>
        </div>

        {/* Rewatch pilots */}
        {onBackToPilots && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button onClick={() => onBackToPilots(null)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem',
                cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", transition: 'color 0.2s',
                letterSpacing: '0.04em', padding: '0.4rem 0' }}
              onMouseOver={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}
              onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}>
              Rewatch Pilots
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TopPicksSelector;
